import { Injectable } from '@angular/core';
import {
  ServiceCaseListDto,
  ServiceCaseDetailDto,
  ServiceCaseLogDto,
  ServiceCaseImageDto,
} from '../../models/customer-service/customer-service-case.model';
import { PagedResultDto } from '../../models/table.model';

@Injectable({
  providedIn: 'root',
})
export class CustomerServiceCaseMapper {

  // ================= Normal Functions =================

  /**
   * Parse a date string into a Date object safely
   */
  parseDate(val: string | null | undefined): Date | null {
    if (!val) return null;
    const parsed = new Date(val);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  /**
   * Translates case priority to Thai label
   */
  translatePriority(priority: string): string {
    switch (priority) {
      case 'low': return 'ต่ำ';
      case 'normal': return 'ปกติ';
      case 'high': return 'สูง';
      case 'urgent': return 'ด่วนที่สุด';
      default: return priority || '-';
    }
  }

  /**
   * Returns Tailwind CSS style classes matching priority
   */
  getPriorityColorClass(priority: string): string {
    switch (priority) {
      case 'low': return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
      case 'normal': return 'bg-blue-100 text-blue-700 dark:bg-blue-950/20 dark:text-blue-400';
      case 'high': return 'bg-amber-100 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400';
      case 'urgent': return 'bg-rose-100 text-rose-700 dark:bg-rose-950/20 dark:text-rose-450';
      default: return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
    }
  }

  /**
   * Translates case channel key to Thai label
   */
  translateChannel(channel: string): string {
    switch (channel) {
      case 'phone': return 'โทรศัพท์';
      case 'walk_in': return 'Walk-in';
      case 'backoffice': return 'ระบบหลังบ้าน';
      default: return channel || '-';
    }
  }

  /**
   * Translates case status to Thai label
   */
  translateStatus(status: string): string {
    switch (status) {
      case 'received': return 'รับเรื่องแล้ว';
      case 'in_progress': return 'กำลังดำเนินการ';
      case 'waiting_customer': return 'รอข้อมูลจากคุณ';
      case 'awaiting_customer_confirm': return 'ดำเนินการเสร็จแล้ว';
      case 'closed': return 'ปิดเรื่องแล้ว';
      case 'cancelled': return 'ยกเลิกแล้ว';
      default: return status || '-';
    }
  }

  /**
   * Returns Tailwind CSS style classes matching status
   */
  getStatusColorClass(status: string): string {
    switch (status) {
      case 'received': return 'bg-sky-50 dark:bg-sky-950/20 text-sky-600 dark:text-sky-400 border border-sky-200/50';
      case 'in_progress': return 'bg-blue-50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 border border-blue-200/50';
      case 'waiting_customer': return 'bg-amber-50 dark:bg-amber-950/20 text-amber-600 dark:text-amber-400 border border-amber-200/50';
      case 'awaiting_customer_confirm': return 'bg-indigo-50 dark:bg-indigo-950/20 text-indigo-600 dark:text-indigo-400 border border-indigo-200/50';
      case 'closed': return 'bg-green-50 dark:bg-green-950/30 text-green-600 dark:text-green-400 border border-green-200/50';
      case 'cancelled': return 'bg-slate-50 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500 border border-slate-200/50';
      default: return 'bg-slate-50 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500 border border-slate-200/50';
    }
  }

  /**
   * Maps chronological case log row
   */
  mapCaseLog(dto: ServiceCaseLogDto): ServiceCaseLogDto {
    const raw = dto as ServiceCaseLogDto & Record<string, any>;
    return {
      id: dto.id ?? raw['Id'] ?? 0,
      actionType: dto.actionType ?? raw['ActionType'] ?? '',
      oldStatus: dto.oldStatus ?? raw['OldStatus'] ?? null,
      newStatus: dto.newStatus ?? raw['NewStatus'] ?? null,
      message: dto.message ?? raw['Message'] ?? '',
      isCustomerVisible: dto.isCustomerVisible ?? raw['IsCustomerVisible'] ?? false,
      attachmentUrl: dto.attachmentUrl ?? raw['AttachmentUrl'] ?? '',
      actorUid: dto.actorUid ?? raw['ActorUid'] ?? 0,
      actorUserName: dto.actorUserName ?? raw['ActorUserName'] ?? '',
      createDate: dto.createDate ?? raw['CreateDate']
    };
  }

  /**
   * Maps saved service case image (read-only after save)
   */
  mapCaseImage(dto: ServiceCaseImageDto): ServiceCaseImageDto {
    const raw = dto as ServiceCaseImageDto & Record<string, any>;
    return {
      id: dto.id ?? raw['Id'] ?? 0,
      serviceCaseId: dto.serviceCaseId ?? raw['ServiceCaseId'] ?? 0,
      serviceCaseLogId: dto.serviceCaseLogId ?? raw['ServiceCaseLogId'] ?? null,
      imagePath: dto.imagePath ?? raw['ImagePath'] ?? '',
      imageExt: dto.imageExt ?? raw['ImageExt'] ?? '',
      imageUrl: dto.imageUrl ?? raw['ImageUrl'] ?? '',
      purpose: dto.purpose ?? raw['Purpose'] ?? '',
      caption: dto.caption ?? raw['Caption'] ?? '',
      isCustomerVisible: dto.isCustomerVisible ?? raw['IsCustomerVisible'] ?? false,
      sortOrder: dto.sortOrder ?? raw['SortOrder'] ?? null,
      createDate: dto.createDate ?? raw['CreateDate'] ?? null,
    };
  }

  /**
   * Maps single service case detail DTO to UI model
   */
  mapCaseDetail(dto: ServiceCaseDetailDto): ServiceCaseDetailDto {
    const raw = dto as ServiceCaseDetailDto & Record<string, any>;
    const logsDto = (dto.logs ?? raw['Logs'] ?? []) as ServiceCaseLogDto[];
    const imagesDto = (dto.images ?? raw['Images'] ?? []) as ServiceCaseImageDto[];
    return {
      id: dto.id ?? raw['Id'] ?? 0,
      caseNo: dto.caseNo ?? raw['CaseNo'] ?? '',
      issueTypeId: dto.issueTypeId ?? raw['IssueTypeId'] ?? null,
      issueTypeName: dto.issueTypeName ?? raw['IssueTypeName'] ?? '',
      issueTypeCode: dto.issueTypeCode ?? raw['IssueTypeCode'] ?? '',
      status: dto.status ?? raw['Status'] ?? 'received',
      statusLabel: dto.statusLabel ?? raw['StatusLabel'] ?? '',
      priority: dto.priority ?? raw['Priority'] ?? 'normal',
      channel: dto.channel ?? raw['Channel'] ?? 'phone',
      subject: dto.subject ?? raw['Subject'] ?? raw['subject'] ?? '',
      contactName: dto.contactName ?? raw['ContactName'] ?? '',
      contactPhone: dto.contactPhone ?? raw['ContactPhone'] ?? '',
      cpUserId: dto.cpUserId ?? raw['CpUserId'] ?? null,
      customerDisplayName: dto.customerDisplayName ?? raw['CustomerDisplayName'] ?? '',
      siteId: dto.siteId ?? raw['SiteId'] ?? null,
      siteName: dto.siteName ?? raw['SiteName'] ?? '',
      assignedUserId: dto.assignedUserId ?? raw['AssignedUserId'] ?? null,
      assignedUserName: dto.assignedUserName ?? raw['AssignedUserName'] ?? '',
      createDate: dto.createDate ?? raw['CreateDate'],
      slaDueAt: dto.slaDueAt ?? raw['SlaDueAt'],
      description: dto.description ?? raw['Description'] ?? '',
      resolutionSummary: dto.resolutionSummary ?? raw['ResolutionSummary'] ?? '',
      customerStatusNote: dto.customerStatusNote ?? raw['CustomerStatusNote'] ?? '',
      assignedAt: dto.assignedAt ?? raw['AssignedAt'] ?? null,
      resolvedAt: dto.resolvedAt ?? raw['ResolvedAt'] ?? null,
      closedAt: dto.closedAt ?? raw['ClosedAt'] ?? null,
      cancelledAt: dto.cancelledAt ?? raw['CancelledAt'] ?? null,
      cancelReason: dto.cancelReason ?? raw['CancelReason'] ?? '',
      logs: logsDto.map(log => this.mapCaseLog(log)),
      images: imagesDto.map(img => this.mapCaseImage(img)),
    };
  }

  /**
   * Maps table query rows response DTO to UI model PagedResultDto
   */
  mapCaseTable(dto: any): PagedResultDto<ServiceCaseListDto> {
    const raw = dto as Record<string, any>;
    const list = (dto.data ?? raw['Data'] ?? []) as ServiceCaseListDto[];
    const total = dto.total ?? raw['Total'] ?? 0;

    return {
      data: list.map(item => {
        const itemRaw = item as ServiceCaseListDto & Record<string, any>;
        return {
          id: item.id ?? itemRaw['Id'] ?? 0,
          caseNo: item.caseNo ?? itemRaw['CaseNo'] ?? '',
          issueTypeId: item.issueTypeId ?? itemRaw['IssueTypeId'] ?? null,
          issueTypeName: item.issueTypeName ?? itemRaw['IssueTypeName'] ?? '',
          issueTypeCode: item.issueTypeCode ?? itemRaw['IssueTypeCode'] ?? '',
          status: item.status ?? itemRaw['Status'] ?? '',
          statusLabel: item.statusLabel ?? itemRaw['StatusLabel'] ?? '',
          priority: item.priority ?? itemRaw['Priority'] ?? 'normal',
          channel: item.channel ?? itemRaw['Channel'] ?? 'phone',
          subject: item.subject ?? itemRaw['Subject'] ?? itemRaw['subject'] ?? '',
          contactName: item.contactName ?? itemRaw['ContactName'] ?? '',
          contactPhone: item.contactPhone ?? itemRaw['ContactPhone'] ?? '',
          cpUserId: item.cpUserId ?? itemRaw['CpUserId'] ?? null,
          customerDisplayName: item.customerDisplayName ?? itemRaw['CustomerDisplayName'] ?? '',
          siteId: item.siteId ?? itemRaw['SiteId'] ?? null,
          siteName: item.siteName ?? itemRaw['SiteName'] ?? '',
          assignedUserId: item.assignedUserId ?? itemRaw['AssignedUserId'] ?? null,
          assignedUserName: item.assignedUserName ?? itemRaw['AssignedUserName'] ?? '',
          createDate: item.createDate ?? itemRaw['CreateDate'],
          slaDueAt: item.slaDueAt ?? itemRaw['SlaDueAt']
        };
      }),
      total: total
    };
  }
}
